import { type AnimationToken, getAnimationByToken } from '@1debit/chime-assets';
import { type LottieRefCurrentProps, useLottie } from 'lottie-react';
import { useEffect, useRef, useState } from 'react';

import { useColorScheme } from '../../hooks/useColorScheme';

export interface AnimationProps {
	token: AnimationToken;
	autoPlay?: boolean;
	loop?: boolean;
	className?: string;
}

export const Animation = ({ token, autoPlay = false, loop = false, className }: AnimationProps) => {
	const { colorScheme } = useColorScheme();
	const animation = getAnimationByToken(token);
	const animationRef = useRef<LottieRefCurrentProps>(null);
	const animationUri = colorScheme === 'light' ? animation.uri : animation.darkModeUri;
	const [animationData, setAnimationData] = useState<object | null>(null);

	const LottieComponent = useLottie({
		animationData: animationData,
		lottieRef: animationRef,
		autoplay: autoPlay,
		loop: loop,
		className: className,
	});

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;

		const loadAnimation = async () => {
			try {
				const response = await fetch(animationUri, { signal });
				if (!response.ok) {
					throw new Error(`Failed to fetch animation: ${response.status} ${response.statusText}`);
				}
				const data = await response.json();
				setAnimationData(data);
			} catch (error) {
				if (error instanceof Error && error.name === 'AbortError') {
					return;
				} else {
					console.error('Failed to load animation:', error);
				}
			}
		};

		loadAnimation();

		return () => {
			controller.abort();
		};
	}, [animationUri]);

	if (!animationData) {
		return null;
	}

	return LottieComponent.View;
};

Animation.displayName = 'Animation';
